import { useEffect, useMemo, useState } from "react";
import { format } from "date-fns";
import {
  ArrowRight,
  Check,
  CheckCircle2,
  Copy,
  GraduationCap,
  Loader2,
  Mail,
  MessageCircle,
  Send,
  Sparkles,
} from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { cn } from "@/lib/utils";
import { toDialableDigits } from "@/constants/countryCodes";
import { useChilds } from "@/features/leads/api/useChilds";
import type { LeadTemplate } from "@/features/whatsapp/api/useLeadTemplates";
import ActiveChildModuleAttachments from "@/features/leads/components/view-lead/components/Overview/components/ChildModuleAttachments/ActiveChildModuleAttachments";
import {
  useCreatePaymentLink,
  usePaidMovePreview,
  usePaymentWhatsappSender,
  useRazorpayStatus,
  useSendExistingPaymentLink,
  type CreatePaymentLinkResult,
} from "../api/useRazorpayMutations";
import { buildPaymentLinkMessage, openWhatsappChat } from "../utils/paymentLinkShare";
import {
  defaultAmountFor,
  formatRupees,
  installmentForAmount,
  parseRupees,
  toPayableDeal,
  type PayableDeal,
  type RawDeal,
} from "../utils/collectPayment";
import RazorpayGlyph from "./RazorpayGlyph";
import type { PaymentLinkLead } from "./SendPaymentLinkForm";

/**
 * "Collect payment" (2026-11-04, owner: "if razorpay is connected i need a
 * seamless experience… if a program is attached, give the payment link with
 * the amount we can type… if paid auto register… really simple").
 *
 * Two screens, nothing else to learn:
 *   1. Program + amount. The amount opens on what is due (the next instalment
 *      on a plan) and can be typed freely up to the balance. "When paid" says
 *      where the student goes on payment (server: razorpay.paid-move.ts).
 *   2. The link: Copy, WhatsApp (the lead's connected number when it can
 *      send, else the counsellor's own WhatsApp), Email.
 * Paid → the payment is recorded on the deal and the deal moves forward on
 * its own; the counsellor gets a toast. A lead with no program yet gets the
 * same "Attach Program" slot the Overview shows, right here, so the whole job
 * happens in this one dialog.
 *
 * Mount inside the lead drawer (the attach slot reads its contexts), with a
 * fresh `key` per open.
 */

const EXPIRY_OPTIONS = [1, 3, 7, 14];
const LINK_PARAM_RE = /link|url|pay/i;
const AMOUNT_PARAM_RE = /amount|amt|fee|due|balance|total/i;
const NAME_PARAM_RE = /name/i;
const PAYMENT_TEMPLATE_RE = /pay|link|fee|invoice/i;

type CreatedLink = CreatePaymentLinkResult["paymentLink"];

export default function CollectPaymentDialog({
  open,
  onOpenChange,
  lead,
  initialDealId = null,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  lead: PaymentLinkLead & { id: number };
  initialDealId?: number | null;
}) {
  const { childs, isLoading } = useChilds({ leadId: lead.id });
  // Frozen deals (paused alternatives) are left out — unless it is the one
  // the counsellor opened this from.
  const deals = useMemo<PayableDeal[]>(
    () =>
      Array.isArray(childs)
        ? (childs as RawDeal[]).filter((c) => !c.isFroze || Number(c.id) === initialDealId).map(toPayableDeal)
        : [],
    [childs, initialDealId],
  );
  const payable = useMemo(() => deals.filter((d) => d.hasProgram && d.pending > 0), [deals]);

  // The pick sticks: a program paid off while this screen is open stays on
  // screen (its amount then reads as over the balance) instead of the dialog
  // jumping to another program under the counsellor.
  const [dealId, setDealId] = useState<number | null>(
    initialDealId != null ? initialDealId : null,
  );
  const picked = dealId != null ? deals.find((d) => d.id === dealId && d.hasProgram) ?? null : null;
  const deal = picked ?? payable[0] ?? null;
  const listed = picked && !payable.some((d) => d.id === picked.id) ? [picked, ...payable] : payable;

  // Snapshot of what the link was made for: if it is paid while the dialog
  // is still open, the deal drops out of `payable` and the share screen
  // must not vanish under the counsellor.
  const [created, setCreated] = useState<{ link: CreatedLink; amount: number; deal: PayableDeal } | null>(null);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-h-[92vh] gap-0 overflow-y-auto p-0 sm:max-w-[480px]"
        style={{ borderRadius: "1.25rem" }}
      >
        <DialogHeader className="px-5 pt-5 pb-1 text-left">
          <DialogTitle className="flex items-center gap-2 text-[17px] font-semibold tracking-[-0.3px] text-[#051912]">
            <span className="flex size-7 items-center justify-center rounded-lg bg-[#eef5ff]">
              <RazorpayGlyph className="size-3.5" />
            </span>
            {created ? "Payment link ready" : "Collect payment"}
          </DialogTitle>
          <DialogDescription className="text-[12.5px] text-[#697571]">
            {lead.name || "This lead"}
            {created ? " · send it any way you like" : " · Razorpay link, recorded on the program when paid"}
          </DialogDescription>
        </DialogHeader>

        <div className="px-5 pb-5 pt-3">
          {created ? (
            <ShareStep
              link={created.link}
              amount={created.amount}
              deal={created.deal}
              lead={lead}
              onDone={() => onOpenChange(false)}
            />
          ) : isLoading ? (
            <div className="flex flex-col gap-2">
              <Skeleton className="h-16 w-full rounded-xl" />
              <Skeleton className="h-12 w-full rounded-xl" />
            </div>
          ) : !deal ? (
            <NoProgram deals={deals} />
          ) : deal.pending <= 0 ? (
            <NoProgram deals={[deal]} />
          ) : (
            <AmountStep
              key={deal.id}
              deals={listed}
              deal={deal}
              onPickDeal={setDealId}
              onCreated={(link, amount) => setCreated({ link, amount, deal })}
            />
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ── Nothing to bill yet ─────────────────────────────────────────────────────

function NoProgram({ deals }: { deals: PayableDeal[] }) {
  const allPaid = deals.length > 0 && deals.every((d) => !d.hasProgram || d.pending <= 0) && deals.some((d) => d.hasProgram);
  if (allPaid) {
    return (
      <div className="flex flex-col items-center gap-2 rounded-xl border border-[#d6eadf] bg-[#f3faf6] px-4 py-6 text-center">
        <CheckCircle2 className="size-6 text-[#2d7a5a]" />
        <p className="text-[13.5px] font-medium text-[#1e302a]">Fully paid</p>
        <p className="text-[12px] text-[#697571]">Nothing is pending on this lead&apos;s programs.</p>
      </div>
    );
  }
  return (
    <div className="flex flex-col gap-3">
      <div className="flex items-start gap-2.5 rounded-xl border border-[#e0c6ff] bg-[#faf8ff] px-3.5 py-3">
        <GraduationCap className="mt-0.5 size-4 shrink-0 text-[#7c3aed]" />
        <p className="text-[12.5px] leading-relaxed text-[#5b21b6]">
          {deals.length === 0
            ? "Attach the program first — its fee becomes the amount to collect."
            : "This deal has no program attached yet. Attach one on the deal (Payments tab → link icon), then collect."}
        </p>
      </div>
      {deals.length === 0 && <ActiveChildModuleAttachments />}
    </div>
  );
}

// ── 1. Program + amount ─────────────────────────────────────────────────────

function AmountStep({
  deals,
  deal,
  onPickDeal,
  onCreated,
}: {
  deals: PayableDeal[];
  deal: PayableDeal;
  onPickDeal: (id: number) => void;
  onCreated: (link: CreatedLink, amount: number) => void;
}) {
  const [text, setText] = useState(() => formatRupees(defaultAmountFor(deal)));
  const [expiresInDays, setExpiresInDays] = useState(7);
  const createLink = useCreatePaymentLink();
  const { data: paidMove } = usePaidMovePreview(deal.id);

  const amount = parseRupees(text);
  const over = amount != null && amount > deal.pending + 0.005;
  const valid = amount != null && !over;
  const inst = amount != null ? installmentForAmount(amount, deal.installments) : null;
  const next = deal.installments[0] ?? null;

  const chips: { label: string; value: number }[] = [];
  if (next && next.outstanding < deal.pending) {
    chips.push({ label: `Instalment #${next.seq}`, value: Math.min(next.outstanding, deal.pending) });
  }
  chips.push({ label: deal.paid > 0 ? "Balance" : "Full fee", value: deal.pending });

  const submit = async () => {
    if (!valid || amount == null || createLink.isPending) return;
    try {
      const res = await createLink.mutateAsync({
        childId: deal.id,
        amount,
        expiresInDays,
        sentVia: [],
        ...(inst ? { installmentId: inst.id } : {}),
      });
      onCreated(res.data.paymentLink, amount);
    } catch {
      /* the hook toasts Razorpay's reason */
    }
  };

  return (
    <form
      className="flex flex-col gap-4"
      onSubmit={(e) => {
        e.preventDefault();
        void submit();
      }}
    >
      {/* Program */}
      <div className="flex flex-col gap-1.5">
        <span className="text-[11.5px] font-medium text-[#697571]">Program</span>
        {deals.map((d) => {
          const active = d.id === deal.id;
          return (
            <button
              key={d.id}
              type="button"
              onClick={() => onPickDeal(d.id)}
              className={cn(
                "flex items-center justify-between gap-3 rounded-xl border px-3.5 py-2.5 text-left transition-colors",
                active ? "border-[#98cdb8] bg-[#eaf5f1]" : "border-[#e2e4e3] bg-white hover:bg-[#f6faf8]",
                deals.length === 1 && "cursor-default",
              )}
            >
              <span className="flex min-w-0 items-center gap-2.5">
                <GraduationCap className={cn("size-4 shrink-0", active ? "text-[#267355]" : "text-[#9ba3a0]")} />
                <span className="min-w-0">
                  <span className="block truncate text-[13.5px] font-medium text-[#1e302a]">{d.name}</span>
                  <span className="block text-[11.5px] text-[#697571]">
                    Fee ₹{formatRupees(d.value)}
                    {d.paid > 0 && <> · paid ₹{formatRupees(d.paid)}</>}
                  </span>
                </span>
              </span>
              <span className="shrink-0 text-right">
                <span className="block text-[13px] font-semibold tabular-nums text-[#c77800]">₹{formatRupees(d.pending)}</span>
                <span className="block text-[10.5px] text-[#9ba3a0]">due</span>
              </span>
            </button>
          );
        })}
      </div>

      {/* Amount */}
      <div className="flex flex-col gap-1.5">
        <label htmlFor="collect-amount" className="text-[11.5px] font-medium text-[#697571]">
          Amount to collect
        </label>
        <div
          className={cn(
            "flex h-12 items-center gap-1.5 rounded-xl border bg-white px-3.5 focus-within:border-[#46a580]",
            over ? "border-[#da2a46]" : "border-[#e2e4e3]",
          )}
        >
          <span className="text-[18px] font-semibold text-[#697571]">₹</span>
          <input
            id="collect-amount"
            autoFocus
            inputMode="decimal"
            autoComplete="off"
            value={text}
            onChange={(e) => setText(e.target.value)}
            onBlur={() => {
              const v = parseRupees(text);
              if (v != null) setText(formatRupees(v));
            }}
            className="w-full bg-transparent text-[20px] font-semibold tabular-nums text-[#051912] outline-none"
            aria-invalid={!valid}
          />
        </div>
        <div className="flex flex-wrap items-center gap-1.5">
          {chips.map((c) => (
            <button
              key={c.label}
              type="button"
              onClick={() => setText(formatRupees(c.value))}
              className={cn(
                "rounded-full border px-2.5 py-1 text-[11.5px] font-medium transition-colors",
                amount === c.value
                  ? "border-[#267355] bg-[#267355] text-white"
                  : "border-[#d9dedb] bg-white text-[#374741] hover:border-[#98cdb8] hover:bg-[#f0faf5]",
              )}
            >
              {c.label} · ₹{formatRupees(c.value)}
            </button>
          ))}
        </div>
        <p className={cn("text-[11.5px]", over || (amount == null && text.trim()) ? "text-[#da2a46]" : "text-[#8a918d]")}>
          {over
            ? `More than the ₹${formatRupees(deal.pending)} due on this program.`
            : amount == null
              ? text.trim()
                ? "Type an amount, e.g. 2,000"
                : "Type any amount up to what is due."
              : inst
                ? `Pays instalment #${inst.seq} (due ${format(new Date(inst.dueDate), "d MMM")}). Any amount up to ₹${formatRupees(deal.pending)}.`
                : `Any amount up to ₹${formatRupees(deal.pending)} — part payments are fine.`}
        </p>
      </div>

      {/* When paid */}
      <div className="flex items-start gap-2.5 rounded-xl bg-[#f3faf6] px-3.5 py-2.5">
        <Sparkles className="mt-0.5 size-3.5 shrink-0 text-[#267355]" />
        <p className="text-[12px] leading-relaxed text-[#1e302a]">
          <span className="font-medium">When paid:</span> recorded on this program automatically
          {paidMove?.moves && paidMove.stageName ? (
            <>
              {" "}and moved to{" "}
              <span className="font-medium">
                {paidMove.stageName}
                {paidMove.statusName ? ` · ${paidMove.statusName}` : ""}
              </span>
            </>
          ) : null}
          . You get a notification.
        </p>
      </div>

      <div className="flex items-center justify-between gap-3">
        <label className="flex items-center gap-1.5 text-[12px] text-[#697571]">
          Link valid for
          <select
            value={expiresInDays}
            onChange={(e) => setExpiresInDays(Number(e.target.value))}
            className="h-8 rounded-lg border border-[#e2e4e3] bg-white px-2 text-[12px] text-[#1e302a] focus:border-[#46a580] focus:outline-none"
          >
            {EXPIRY_OPTIONS.map((d) => (
              <option key={d} value={d}>
                {d === 1 ? "1 day" : `${d} days`}
              </option>
            ))}
          </select>
        </label>
        <Button
          type="submit"
          variant="primary"
          disabled={!valid || createLink.isPending}
          className="h-10 gap-1.5 rounded-xl px-5 text-[13.5px] font-semibold"
        >
          {createLink.isPending ? (
            <Loader2 className="size-4 animate-spin" />
          ) : (
            <>
              Create link{amount != null && valid ? ` · ₹${formatRupees(amount)}` : ""}
              <ArrowRight className="size-4" />
            </>
          )}
        </Button>
      </div>
    </form>
  );
}

// ── 2. Share ────────────────────────────────────────────────────────────────

/** Only a template whose NAME says it is for payments is picked for the counsellor. */
function guessTemplate(templates: LeadTemplate[]): LeadTemplate | null {
  return templates.find((t) => t.params.length > 0 && PAYMENT_TEMPLATE_RE.test(t.name)) ?? null;
}

function guessLinkParam(t: LeadTemplate): string {
  return t.params.find((p) => LINK_PARAM_RE.test(p)) ?? t.params[t.params.length - 1] ?? "";
}

function ShareStep({
  link,
  amount,
  deal,
  lead,
  onDone,
}: {
  link: CreatedLink;
  amount: number;
  deal: PayableDeal;
  lead: PaymentLinkLead & { id: number };
  onDone: () => void;
}) {
  const url = link.rzpShortUrl;
  const message = buildPaymentLinkMessage({ name: lead.name, amount, url, expiresAt: link.expiresAt });
  const digits = toDialableDigits(lead.phone, lead.phoneCountryCode);
  const { data: rzp } = useRazorpayStatus();
  const { data: sender, isLoading: senderLoading } = usePaymentWhatsappSender(lead.id, !!lead.phone);
  const { data: paidMove } = usePaidMovePreview(deal.id);
  const send = useSendExistingPaymentLink();
  const [sent, setSent] = useState<Record<string, boolean>>({});
  const [copied, setCopied] = useState(false);

  // Window closed but templates exist → an approved template. Picked for the
  // counsellor ONLY when its name says it is for payments; every variable is
  // shown and editable, the link's slot can be changed, and the message is
  // previewed exactly as it will go — Cloud API variables are just {{1}},
  // {{2}}, so nothing may be guessed silently.
  const templates = useMemo(() => (sender?.templates ?? []).filter((t) => t.params.length > 0), [sender]);
  const [templateKey, setTemplateKey] = useState("");
  const [linkParam, setLinkParam] = useState("");
  const [vars, setVars] = useState<Record<string, string>>({});
  const template = templates.find((t) => String(t.id) === templateKey) ?? null;
  const fillFor = (t: LeadTemplate, link: string, keep: Record<string, string> = {}) => {
    const next: Record<string, string> = {};
    for (const p of t.params) {
      if (p === link) continue;
      if (keep[p]?.trim()) next[p] = keep[p]!;
      else if (NAME_PARAM_RE.test(p) && !/course|program|batch/i.test(p)) next[p] = lead.name ?? "";
      else if (AMOUNT_PARAM_RE.test(p) && !/date|count|number|install/i.test(p)) next[p] = formatRupees(amount);
      else next[p] = t.sampleValues?.[p] ?? "";
    }
    return next;
  };
  function pickTemplate(t: LeadTemplate) {
    const link = guessLinkParam(t);
    setTemplateKey(String(t.id));
    setLinkParam(link);
    setVars(fillFor(t, link));
  }
  useEffect(() => {
    if (templateKey || !templates.length) return;
    const t = guessTemplate(templates);
    if (t) pickTemplate(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [templates]);
  const otherParams = template ? template.params.filter((p) => p !== linkParam) : [];
  const templateReady = !!template && !!linkParam && otherParams.every((p) => (vars[p] ?? "").trim());
  let preview = template?.body ?? "";
  for (const p of template?.params ?? []) {
    const value = p === linkParam ? url : vars[p] || `{{${p}}}`;
    const token = new RegExp(`\\{\\{\\s*${p.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")}\\s*\\}\\}`, "g");
    preview = preview.replace(token, () => value);
  }

  const canChat = !!sender?.available && sender.windowOpen;
  const canTemplate = !!sender?.available && !sender.windowOpen && templates.length > 0;

  const copy = async (text: string, what: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      toast.success(`${what} copied`);
      window.setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error("Could not copy — select the link and copy it");
    }
  };

  const sendVia = (via: "whatsapp" | "email") => {
    const whatsapp =
      via !== "whatsapp"
        ? undefined
        : canChat
          ? ({ mode: "text" } as const)
          : template
            ? ({
                mode: "template",
                templateId: template.id,
                templateName: template.name,
                variables: Object.fromEntries(otherParams.map((p) => [p, vars[p] ?? ""])),
                linkParam,
              } as const)
            : undefined;
    send.mutate(
      { id: link.id, via, whatsapp },
      { onSuccess: () => setSent((s) => ({ ...s, [via]: true })) },
    );
  };

  return (
    <div className="flex flex-col gap-4">
      {/* The link */}
      <div className="rounded-xl border border-[#d6eadf] bg-[#f3faf6] px-3.5 py-3">
        <div className="flex items-center justify-between gap-2">
          <span className="flex items-center gap-1.5 text-[12px] font-medium text-[#267355]">
            <CheckCircle2 className="size-3.5" /> ₹{formatRupees(amount)} · {deal.name}
          </span>
          {link.expiresAt && (
            <span className="text-[11px] text-[#697571]">valid till {format(new Date(link.expiresAt), "d MMM")}</span>
          )}
        </div>
        <div className="mt-2 flex items-center gap-2">
          <input
            readOnly
            value={url}
            onFocus={(e) => e.currentTarget.select()}
            className="h-10 min-w-0 flex-1 rounded-lg border border-[#d9dedb] bg-white px-3 text-[13px] text-[#1e302a] outline-none"
          />
          <Button
            type="button"
            variant="primary"
            onClick={() => copy(url, "Link")}
            className="h-10 shrink-0 gap-1.5 rounded-lg px-3.5 text-[13px] font-semibold"
          >
            {copied ? <Check className="size-4" /> : <Copy className="size-4" />}
            Copy
          </Button>
        </div>
      </div>

      {/* Send it */}
      <div className="flex flex-col gap-2">
        <span className="text-[11.5px] font-medium text-[#697571]">Send it</span>

        {lead.phone && senderLoading ? (
          <Skeleton className="h-11 w-full rounded-xl" />
        ) : canChat || canTemplate ? (
          <div className={cn("flex flex-col gap-2", canTemplate && "rounded-xl border border-[#e2e4e3] bg-white p-2.5")}>
            {canTemplate && (
              <div className="flex flex-col gap-1.5">
                <p className="text-[11px] text-[#8a918d]">
                  No reply from them in 24 h, so WhatsApp needs an approved template.
                </p>
                <select
                  value={templateKey}
                  onChange={(e) => {
                    const t = templates.find((x) => String(x.id) === e.target.value);
                    if (t) pickTemplate(t);
                  }}
                  className="h-9 rounded-lg border border-[#e2e4e3] bg-white px-2 text-[12.5px] focus:border-[#46a580] focus:outline-none"
                >
                  {!template && <option value="">Pick a template…</option>}
                  {templates.map((t) => (
                    <option key={String(t.id)} value={String(t.id)}>
                      {t.name.replace(/_/g, " ")}
                    </option>
                  ))}
                </select>
                {template && template.params.length > 1 && (
                  <label className="flex items-center gap-2 text-[12px]">
                    <span className="shrink-0 text-[#697571]">Link goes into</span>
                    <select
                      value={linkParam}
                      onChange={(e) => {
                        setLinkParam(e.target.value);
                        setVars((v) => fillFor(template, e.target.value, v));
                      }}
                      className="h-8 flex-1 rounded-lg border border-[#e2e4e3] bg-white px-2 text-[12px] focus:border-[#46a580] focus:outline-none"
                    >
                      {template.params.map((p) => (
                        <option key={p} value={p}>{`{{${p}}}`}</option>
                      ))}
                    </select>
                  </label>
                )}
                {otherParams.map((p) => (
                  <label key={p} className="flex items-center gap-2 text-[12px]">
                    <span className="w-[86px] shrink-0 truncate font-mono text-[11px] text-[#697571]">{`{{${p}}}`}</span>
                    <input
                      value={vars[p] ?? ""}
                      onChange={(e) => setVars((v) => ({ ...v, [p]: e.target.value }))}
                      className="h-8 flex-1 rounded-lg border border-[#e2e4e3] px-2.5 text-[12px] focus:border-[#46a580] focus:outline-none"
                    />
                  </label>
                ))}
                {template && preview && (
                  <div className="whitespace-pre-wrap rounded-lg bg-[#d9fdd3] px-2.5 py-2 text-[12px] leading-[17px] text-[#111b21]">
                    {preview}
                  </div>
                )}
              </div>
            )}
            <ActionRow
              icon={<MessageCircle className="size-4" />}
              tone="green"
              title={sent.whatsapp ? "Sent on WhatsApp" : canChat ? "Send on WhatsApp" : "Send WhatsApp template"}
              hint={sender?.label}
              done={!!sent.whatsapp}
              busy={send.isPending && send.variables?.via === "whatsapp"}
              disabled={canTemplate && !templateReady}
              onClick={() => sendVia("whatsapp")}
            />
          </div>
        ) : null}

        {digits && (
          <ActionRow
            icon={<Send className="size-4" />}
            tone="green"
            title={canChat || canTemplate ? "Open in my WhatsApp" : "Send on WhatsApp"}
            hint="Opens your WhatsApp with the message typed in"
            onClick={() => openWhatsappChat(digits, message)}
          />
        )}

        {lead.email && (
          <ActionRow
            icon={<Mail className="size-4" />}
            tone="blue"
            title={sent.email ? "Sent by email" : "Send by email"}
            hint={lead.email}
            done={!!sent.email}
            busy={send.isPending && send.variables?.via === "email"}
            onClick={() => sendVia("email")}
          />
        )}

        <button
          type="button"
          onClick={() => copy(message, "Message")}
          className="self-start text-[12px] font-medium text-[#267355] underline-offset-2 hover:underline"
        >
          Copy the full message instead
        </button>

        {rzp?.data?.notifyCustomer && (
          <p className="text-[11.5px] text-[#697571]">Razorpay has also sent it to the student&apos;s email / phone.</p>
        )}
      </div>

      <div className="flex items-center justify-between gap-3 border-t border-[#eef0ef] pt-3">
        <p className="text-[11.5px] leading-snug text-[#697571]">
          When paid it&apos;s recorded{paidMove?.moves && paidMove.stageName ? ` and moved to ${paidMove.stageName}` : ""} — you&apos;ll be
          notified.
        </p>
        <Button type="button" variant="outline" onClick={onDone} className="h-9 shrink-0 rounded-lg px-4 text-[13px]">
          Done
        </Button>
      </div>
    </div>
  );
}

function ActionRow({
  icon,
  title,
  hint,
  tone,
  done,
  busy,
  disabled,
  onClick,
}: {
  icon: React.ReactNode;
  title: string;
  hint?: string;
  tone: "green" | "blue";
  done?: boolean;
  busy?: boolean;
  disabled?: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={busy || disabled}
      className="flex items-center gap-3 rounded-xl border border-[#e2e4e3] bg-white px-3 py-2.5 text-left transition-colors hover:border-[#98cdb8] hover:bg-[#f6faf8] disabled:cursor-not-allowed disabled:opacity-60"
    >
      <span
        className={cn(
          "flex size-8 shrink-0 items-center justify-center rounded-lg",
          tone === "green" ? "bg-[#e8f7ef] text-[#1d8f55]" : "bg-[#eef5ff] text-[#1f6fd1]",
        )}
      >
        {busy ? <Loader2 className="size-4 animate-spin" /> : done ? <Check className="size-4" /> : icon}
      </span>
      <span className="min-w-0">
        <span className="block text-[13px] font-medium text-[#1e302a]">{title}</span>
        {hint && <span className="block truncate text-[11.5px] text-[#8a918d]">{hint}</span>}
      </span>
    </button>
  );
}
